import { use } from 'react'
import { useParams as useNextParams, useSearchParams as useNextSearchParams } from 'next/navigation'
import type { ReadonlyURLSearchParams } from 'next/navigation'

/** Props Next.js 15 passes to client `page.tsx` files (async dynamic APIs). */
export type ClientPageProps = {
    params: Promise<Record<string, string | string[] | undefined>>
    searchParams: Promise<Record<string, string | string[] | undefined>>
}

/**
 * Unwrap `params` and `searchParams` from the page segment so dev does not log
 * sync-dynamic-apis warnings when those promises are enumerated.
 * Call at the top of every client page default export.
 */
export function useClientPageProps({ params, searchParams }: ClientPageProps): void {
    use(params)
    use(searchParams)
}

/**
 * Thin alias for `useParams()`. Do not wrap the result in `use(Promise.resolve(...))` — React 19
 * rejects uncached promises created inside hooks ("Creating promises inside a Client Component…").
 * Segment `params` from **page props** are unwrapped via `useClientPageProps` instead.
 */
export function useUnwrappedParams<T extends Record<string, string | string[] | undefined>>() {
    return useNextParams() as T
}

/** Thin alias for `useSearchParams()` — same rationale as `useUnwrappedParams`. */
export function useUnwrappedSearchParams(): ReadonlyURLSearchParams | null {
    return useNextSearchParams()
}
